/**
 * @fileoverview Header component with hamburger navigation and responsive layout.
 * On mobile, displays in two lines: hamburger + budget name on line 1, user controls
 * on line 2. On desktop, displays in a single line.
 */

import { useState } from 'react';
import LogoutButton from '../Auth/LogoutButton';
import HeaderTitle from './HeaderTitle';
import NavigationMenu from './NavigationMenu';
import { PresenceIndicator } from '../Realtime';
import styles from './Header.module.css';

/**
 * Header Component
 *
 * Displays a header with hamburger menu navigation, budget selector, and logout button.
 *
 * Props:
 * - currentPage (string): Current page being viewed
 * - onNavigate (function): Called when user selects a menu item
 * - budgets (array): Array of budgets user has access to
 * - selectedBudget (object): Currently selected budget
 * - onBudgetChange (function): Called when user changes budget
 * - userName (string): User's name for display
 * - userRole (string): User's role in the current budget (owner, elevated_member, member)
 * - currentUserId (number): Current user's ID for presence indicator
 */
function Header({ currentPage, onNavigate, budgets, selectedBudget, onBudgetChange, userName, userRole, currentUserId, isAdmin }) {
    const [menuOpen, setMenuOpen] = useState(false);

    const handleMenuClick = (page) => {
        onNavigate(page);
        setMenuOpen(false); // Close menu after selection
    };

    // Group and sort budgets: active first (alphabetically), then ceased (alphabetically)
    const groupAndSortBudgets = (budgets) => {
        const active = budgets.filter(b => !b.ceaseDate)
            .sort((a, b) => a.budgetName.localeCompare(b.budgetName));
        const ceased = budgets.filter(b => b.ceaseDate)
            .sort((a, b) => a.budgetName.localeCompare(b.budgetName));
        return { active, ceased };
    };

    return (
        <div className={styles.header}>
            {/* Top Row - Hamburger and Budget Name (mobile) / Full header (desktop) */}
            <div className={styles.topRow}>
                {/* Hamburger Icon */}
                <button
                    onClick={() => setMenuOpen(!menuOpen)}
                    className={styles.hamburger}
                    aria-label="Menu"
                >
                    ☰
                </button>

                {/* Page Title */}
                <HeaderTitle currentPage={currentPage} selectedBudget={selectedBudget} />
            </div>

            {/* Bottom Row - User Controls (mobile) / Part of top row (desktop) */}
            <div className={styles.controls}>
                {/* Presence Indicator - shows who else is viewing this budget */}
                <PresenceIndicator currentUserId={currentUserId} />

                {/* User Name */}
                {userName && (
                    <span className={styles.userName}>
                        {userName}
                    </span>
                )}

                {/* Budget Selector - only show when user has multiple budgets */}
                {budgets && budgets.length > 1 && selectedBudget && (() => {
                    const { active, ceased } = groupAndSortBudgets(budgets);
                    return (
                        <select
                            value={selectedBudget.budgetKey}
                            onChange={(e) => onBudgetChange(e.target.value)}
                            className={styles.budgetSelect}
                        >
                            {active.map(budget => (
                                <option
                                    key={budget.budgetKey}
                                    value={budget.budgetKey}
                                    className={styles.budgetOption}
                                >
                                    {budget.budgetName}
                                </option>
                            ))}
                            {ceased.length > 0 && (
                                <>
                                    <option disabled className={styles.separator}>
                                        ──────────
                                    </option>
                                    {ceased.map(budget => (
                                        <option
                                            key={budget.budgetKey}
                                            value={budget.budgetKey}
                                            className={styles.budgetOptionCeased}
                                        >
                                            {budget.budgetName} (CEASED)
                                        </option>
                                    ))}
                                </>
                            )}
                        </select>
                    );
                })()}

                {/* Logout Button */}
                <LogoutButton />
            </div>

            {/* Dropdown Menu */}
            <NavigationMenu
                menuOpen={menuOpen}
                currentPage={currentPage}
                handleMenuClick={handleMenuClick}
                userRole={userRole}
                setMenuOpen={setMenuOpen}
                isAdmin={isAdmin}
            />
        </div>
    );
}

export default Header;
